import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClientInferRequest } from "@ts-rest/core";
import { FirebaseError } from "firebase/app";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

import { client } from "~/api/client";
import type { apiContract } from "~/api/contract";
import { isApiError } from "~/api/error";
import { suppliersKeys } from "~/api/suppliers/keys";
import { tillsKeys } from "~/api/tills/keys";
import { auth } from "~/lib/firebase";
import { useAppDispatch } from "~/providers/app";
import { authActions } from "~/providers/store/auth";
import { logger } from "~/services/logger";

import { useSendWebEvent } from "../useSendWebEvent";

import { profilesKeys } from "./profiles";

type ImpersonateRequest = ClientInferRequest<
  typeof apiContract.v1.pos.auth.till
>;

function useImpersonateTill() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      body: ImpersonateRequest["body"] & { tillId: string }
    ) => {
      const res = await client.v1.pos.auth.till({
        body
      });
      if (res.status === 200) {
        return res.body;
      }
      throw res;
    },
    onSuccess: async (emulateInfo) => {
      authActions.impersonateTill({
        moduleId: emulateInfo.moduleId,
        userId: emulateInfo.moduleId,
        schoolId: emulateInfo.schoolId,
        tillId: emulateInfo.tillId,
        partnerId: emulateInfo.partnerId
      });

      // invalidate queries that depend on moduleId, schoolId, partnerId
      await queryClient.invalidateQueries({
        queryKey: tillsKeys.list({})
      });
      await queryClient.invalidateQueries({ queryKey: profilesKeys._def });
      await queryClient.invalidateQueries({ queryKey: suppliersKeys.all });

      navigate("/profiles");
    }
  });
}

type FirebaseLoginArgs = { email: string; password: string };
type LoginArgs = FirebaseLoginArgs &
  ClientInferRequest<typeof apiContract.v2.pos.auth.login>["body"];

export function useLogin() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const sendWebEvent = useSendWebEvent();

  return useMutation({
    mutationFn: async ({ email, password, deviceId, tillId }: LoginArgs) => {
      try {
        // Authenticate on the client with Firebase; the ID token is then
        // auto-injected as `x-id-token` on the v2 request by the API client.
        await signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        // Firebase auth domains aren't reachable/allowlisted on this network —
        // fall back to the legacy credential-based login. Any other Firebase
        // error (e.g. wrong password) is surfaced to the user unchanged.
        if (
          error instanceof FirebaseError &&
          error.code === "auth/network-request-failed"
        ) {
          logger.warn(
            "[AUTH] Firebase unreachable — falling back to legacy login"
          );

          const legacyRes = await client.v1.pos.auth.login({
            body: { email, password, deviceId, tillId },
            headers: { authorization: "" }
          });

          if (legacyRes.status === 200) {
            return legacyRes.body;
          }

          throw legacyRes;
        }

        throw error;
      }

      const res = await client.v2.pos.auth.login({
        body: { deviceId, tillId }
      });

      if (res.status === 200) {
        return res.body;
      }

      // Device not authorized for this Firebase user — drop the Firebase
      // session before surfacing the error.
      if (res.status === 403) {
        await signOut(auth);
      }

      throw res;
    },
    onSuccess: (data) => {
      authActions.login({
        // Legacy fallback returns a bearer token; the Firebase path does not.
        token:
          "token" in data && typeof data.token === "string"
            ? data.token
            : undefined,
        partnerId: data.partnerId,
        isKanplaAdmin: data.isKanplaAdmin
      });

      if (data.status === "assigned") {
        dispatch({
          type: "DEVICE_ASSIGN_TILL",
          payload: data.tillId
        });

        sendWebEvent({
          type: "DEVICE_DATA_SET_REQUEST",
          payload: { tillId: data.tillId }
        });

        authActions.assignTill({
          moduleId: data.moduleId,
          userId: data.userId,
          schoolId: data.schoolId,
          tillId: data.tillId
        });

        navigate("/profiles");
        return;
      }

      navigate("/license");
    },
    onError: (error) => {
      if (isApiError(error) && error.status === 401) {
        logger.warn("[AUTH] Login rejected: invalid credentials", {
          error_code: error.body.code
        });
        return;
      }
      if (error instanceof FirebaseError) {
        logger.warn("[AUTH] Login rejected: Firebase auth error", {
          error_code: error.code
        });
        return;
      }
      logger.error(error, "[AUTH] Login failed unexpectedly");
    }
  });
}

export { useImpersonateTill };
